import { apiClient } from '../../../app/api/apiClient';

// Wired to routers/admin_roles.py — /api/admin/tenants/{tenant_id}/roles*.
// Only endpoints confirmed to actually exist on the backend are
// implemented here:
//   GET    /api/admin/tenants/{tenant_id}/roles
//   GET    /api/admin/tenants/{tenant_id}/roles/{role_id}
//   POST   /api/admin/tenants/{tenant_id}/roles
//   PATCH  /api/admin/tenants/{tenant_id}/roles/{role_id}/deactivate
// There is no general PATCH for name/description and no DELETE — do
// not add UI affordances implying either exists.

export interface RoleApiResponse {
  id: number;
  tenant_id: number;
  name: string;
  slug: string;
  description: string | null;
  is_system: boolean;
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_at: string | null;
  updated_at: string | null;
}

export interface RoleListApiResponse {
  success: boolean;
  roles: RoleApiResponse[];
}

export interface RoleDetailApiResponse {
  success: boolean;
  role: RoleApiResponse;
}

export async function getRoles(tenantId: string | number) {
  const response = await apiClient.get(`/api/admin/tenants/${tenantId}/roles`);

  return (response.data as RoleListApiResponse).roles;
}

export async function getRole(tenantId: string | number, roleId: string | number) {
  const response = await apiClient.get(
    `/api/admin/tenants/${tenantId}/roles/${roleId}`,
  );

  return (response.data as RoleDetailApiResponse).role;
}

export interface CreateRolePayload {
  name: string;
  slug: string;
  description?: string;
  is_system?: boolean;
}

/**
 * Creates a role through the real backend.
 * POST /api/admin/tenants/{tenant_id}/roles
 */
export async function createRole(tenantId: string | number, payload: CreateRolePayload) {
  const response = await apiClient.post(
    `/api/admin/tenants/${tenantId}/roles`,
    payload,
  );

  return (response.data as RoleDetailApiResponse).role;
}

/**
 * Deactivates a role through the real backend.
 * PATCH /api/admin/tenants/{tenant_id}/roles/{role_id}/deactivate
 *
 * NOT CALLED FROM ANY UI YET — left unwired intentionally.
 * infrastructure/database/role_repository.py's deactivate() runs
 * `UPDATE roles SET is_active = 0 ...`, but the real `roles` table
 * (database/main.py) has no `is_active` column — confirmed by
 * comparing the CREATE TABLE statement against the repository SQL.
 * Calling this against the real backend today will fail with a
 * Postgres "column is_active does not exist" error, not a graceful
 * app-level response. Do not wire this into any button until the
 * `roles.is_active / metadata / updated_at` migration (tracked
 * separately as a backend follow-up) has actually been applied.
 */
export async function deactivateRole(tenantId: string | number, roleId: string | number) {
  const response = await apiClient.patch(
    `/api/admin/tenants/${tenantId}/roles/${roleId}/deactivate`,
  );

  return (response.data as RoleDetailApiResponse).role;
}
